import React, { useEffect, useContext, useState, useRef } from "react"
import { UserContext } from "../context/userContext"
import { Device } from "@twilio/voice-sdk"
import { Dialog } from "primereact/dialog"
import { trApiAudioRemoveHangupEvent } from "../pages/TelerecoveryServices/telerecoveryApi"
import { telerecoveryApiUrl } from "../config/microservicesUrls"
import { HubConnectionBuilder } from "@microsoft/signalr"
import { Button } from "primereact/button"
import hangup_icon from "../resources/hangup.svg"
import mic_icon from "../resources/mic.svg"
import mic_off_icon from "../resources/mic_off.svg"
import { cleanPhoneNumber } from "../pages/TelerecoveryServices/Utils"

const CallStatus = () => {
  const userCtx = useContext(UserContext)
  const {
    audioToken,
    setCallStatus,
    callStatus,
    isOnCall,
    setIsOnCall,
    isDeviceReady,
    setIsDeviceReady,
    startCallEvent,
    setStartCallEvent,
    hangupEvent,
    setHangupEvent,
    muteOnOffEvent,
    setMuteOnOffEvent,
    isMuted,
    setIsMuted,
    setCallError,
    setShowCarrierBlockCallError,
  } = userCtx.call

  const [device, setDevice] = useState(null)
  const [activeCall, setActiveCall] = useState(null)
  const callParamsRef = useRef(null)

  useEffect(() => {
    if (!audioToken) return

    if (device) {
      device.destroy()
    }

    const newDevice = new Device(audioToken)

    newDevice.on("incoming", (call) => {
      call.accept()
      setActiveCall(call)
      setCallStatus("Call in progress.")
      setIsOnCall(true)
    })

    newDevice.on("disconnect", () => {
      setActiveCall(null)
      setCallStatus("No call in progress.")
      setIsOnCall(false)
    })

    newDevice.register().catch((error) => {
      console.error("Error registering device:", error)
    })

    setDevice(newDevice)

    // Cleanup
    return () => {
      newDevice.destroy()
    }
  }, [audioToken])

  useEffect(() => {
    if (device) {
      device.on("incoming", (call) => {
        // immediately accepts incoming call
        call.accept()
        setCallStatus(call.status())
        setCallError("")
        setShowCarrierBlockCallError(false)
      })

      device.on("registered", () => {
        setIsDeviceReady(true)
        accept()
      })

      device.on("error", (twilioError, call) => {
        console.log("An error has occurred: ", twilioError)
      })

      device.on("disconnect", (call) => {
        hangup()
      })

      const registerDevice = async () => {
        try {
          await device.register()
        } catch (error) {
          console.error("Error registering device")
        }
      }
      registerDevice()
    }
  }, [device])

  useEffect(() => {
    setCallError("")
    setShowCarrierBlockCallError(false)
  }, [userCtx.current_participant_id])

  useEffect(() => {
    const signalrConnection = new HubConnectionBuilder()
      .withUrl(telerecoveryApiUrl() + "/notificationHub")
      .withAutomaticReconnect()
      .build()

    signalrConnection
      .start()
      .then(() => {
        signalrConnection.on("CallError", (errorCode, fromNumber, toNumber) => {
          const callParams = callParamsRef.current
          if (
            cleanPhoneNumber(decodeURIComponent(fromNumber)) ===
              cleanPhoneNumber(callParams.toClient) &&
            cleanPhoneNumber(decodeURIComponent(toNumber)) ===
              cleanPhoneNumber(callParams.phoneNumber)
          ) {
            if (decodeURIComponent(errorCode) === "32017") {
              setShowCarrierBlockCallError(true)
            } else {
              setCallError("Error connecting call. Please try again.")
            }
            setIsOnCall(false)
          }
        })
      })
      .catch((e) => console.log("notificationHub Connection failed: ", e))

    // cleanup
    return () => {
      signalrConnection.stop()
    }
  }, [])

  useEffect(() => {
    if (startCallEvent) {
      startCall(startCallEvent)
      setStartCallEvent(false) // Reset event
    }
  }, [startCallEvent, setStartCallEvent])

  useEffect(() => {
    if (hangupEvent) {
      hangup()
      setHangupEvent(false) // Reset event
    }
  }, [hangupEvent, setHangupEvent])

  useEffect(() => {
    if (muteOnOffEvent) {
      muteOnOff()
      setMuteOnOffEvent(false) // Reset event
    }
  }, [muteOnOffEvent, setMuteOnOffEvent])

  const accept = async () => {
    if (
      userCtx.incomingCall_PickedUp === "NO" &&
      userCtx.incomingCall_EventId
    ) {
      if (activeCall) {
        hangup()
      }
      await connectToQueue(
        userCtx.incomingCall_QueueId,
        userCtx.incomingCall_EventId,
      )
    }
  }

  const connectToQueue = async (queueId, eventId) => {
    let call = await device.connect({
      params: {
        To: queueId,
        From: "1", //hardcoded in druppal version as well
      },
    })
    onConnected(call)

    // Send request to cancel auto-hangup
    removeHangupEvent(eventId)
  }

  const removeHangupEvent = (eventId) => {
    trApiAudioRemoveHangupEvent(
      userCtx.tokens.getTelerecoveryToken(),
      eventId,
    ).then(
      (response) => {},
      (err) => console.log(err),
    )
  }

  const onConnected = (call) => {
    setCallStatus("Call in progress.")
    setCallError("")
    setShowCarrierBlockCallError(false)
    setActiveCall(call)
    userCtx.setIncomingCallPickedUp("SI")
    setIsOnCall(true)
    call.on("error", (error) => {
      console.log(error)
    })
    call.on("reject", () => {
      setCallError("The call was rejected.")
    })
    call.on("disconnect", (call) => {
      hangup()
    })
  }

  const startCall = async (params) => {
    callParamsRef.current = params

    if (!device) {
      setCallError("Device not initialized. Please try again.")
      return
    }
    if (!isDeviceReady) {
      setCallError("Device not ready. Please wait and try again.")
      return
    }

    try {
      const call = await device.connect({ params })

      call.on("disconnect", (call) => {
        setActiveCall(null)
        setCallStatus("No call in progress.")
        setIsOnCall(false)
      })

      setActiveCall(call)
      setCallStatus("Call in progress.")
      setIsOnCall(true)
    } catch (error) {
      setCallError("Error connecting call. Please try again.")
      setIsOnCall(false)
    }
  }

  const hangup = () => {
    if (device) device.disconnectAll()
    if (activeCall) activeCall.disconnect()
    setActiveCall(null)
    setCallStatus("No call in progress.")
    setIsOnCall(false)
  }

  const muteOnOff = () => {
    if (activeCall) {
      activeCall.mute(!isMuted)
      setIsMuted(!isMuted)
    }
  }

  const containerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#292929",
    borderRadius: "12px",
    padding: "10px 20px",
    width: "300px",
    color: "white",
    fontFamily: "Arial, sans-serif",
    fontSize: "14px",
    float: "right",
    marginRight: "25px",
  }

  const infoStyle = {
    display: "flex",
    flexDirection: "column",
  }

  const usernameStyle = {
    fontWeight: "bold",
  }

  const statusStyle = {
    fontSize: "12px",
    color: "#cccccc",
  }

  return (
    <Dialog
      visible={isOnCall}
      header="Call Details"
      modal={false}
      style={{
        background: "transparent", // Make the dialog background transparent
        boxShadow: "none", // Remove any shadows
        border: "none", // Remove border
      }}
      className="callstatus-dialog" // Optional class for additional styles
    >
      <div style={containerStyle}>
        <div style={infoStyle}>
          <span style={usernameStyle}>
            {startCallEvent && startCallEvent.participantName}
          </span>
          <span style={statusStyle}>{callStatus}</span>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            style={{
              borderRadius: "50% !important",
              border: "1px solid white",
              backgroundColor: !isMuted ? "rgb(41, 41, 41)" : "white",
            }}
            icon={<img src={!isMuted ? mic_icon : mic_off_icon} alt="mic" />}
            className={
              "callstatus-dialog-btn " + (!isMuted ? "mute" : "unmute")
            }
            onClick={() => muteOnOff()}
          />
          <Button
            style={{ borderRadius: "50%" }}
            icon={<img src={hangup_icon} alt="mic" />}
            className={
              "callstatus-dialog-btn " + (!isMuted ? "mute" : "unmute")
            }
            onClick={() => {
              hangup()
            }}
          />
        </div>
      </div>
    </Dialog>
  )
}

export default CallStatus
